import {
  FloatingContext,
  FloatingFocusManager,
  FloatingPortal,
  UseFloatingReturn,
} from '@floating-ui/react'
import { forwardRef, ReactNode } from 'react'
import { TSizeAll } from 'src/types'
import { FormComponentBodySlotUI } from '../FormComponentUI/FormComponentBodySlotUI'
import { FormComponentBodyUI } from '../FormComponentUI/FormComponentBodyUI'
import { FormComponentErrorMessageUI } from '../FormComponentUI/FormComponentErrorMessageUI'
import { FormComponentLabelUI } from '../FormComponentUI/FormComponentLabelUI'
import { FormComponentWrapperUI } from '../FormComponentUI/FormComponentWrapperUI'
import styles from './SelectBase.module.scss'
import { SelectChevron } from './SelectChevron'
import { SelectContext, SelectContextValue } from './SelectContext'
import { SelectDropdown } from './SelectDropdown'

export interface CommonSelectProps {
  placeholder?: ReactNode
  children: JSX.Element[]
  error?: boolean | string
  disabled?: boolean
  label?: ReactNode
  isTransparent?: boolean
  size?: TSizeAll
  defaultIsOpen?: boolean
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void
  style?: React.CSSProperties
  id: string
  /**
   * The maximum height of the dropdown.
   * @default all available height
   * @example '200px'
   */
  dropdownMaxHeight?: string
  /**
   * Optional custom className
   */
  className?: string
  /**
   * Optional slot for additional content before the dropdown.
   */
  slots?: {
    before?: ReactNode
  }
  required?: boolean
}

export interface SelectBaseProps extends CommonSelectProps {
  floatingStyles?: React.CSSProperties
  context: FloatingContext
  selectedChildren: ReactNode
  referenceProps: Record<string, unknown>
  floatingProps: Record<string, unknown>
  elementsRef: React.MutableRefObject<(HTMLElement | null)[]>
  labelsRef: React.MutableRefObject<(string | null)[]>
  selectContext: SelectContextValue
  isOpen: boolean
  refs: UseFloatingReturn['refs']
  canGrowVertically?: boolean
}

export const SelectBase = forwardRef<HTMLDivElement, SelectBaseProps>(
  (
    {
      label,
      error,
      disabled,
      isTransparent,
      size = 'md',
      isOpen,
      refs,
      floatingStyles,
      context,
      children,
      selectedChildren,
      referenceProps,
      floatingProps,
      elementsRef,
      labelsRef,
      selectContext,
      style,
      className,
      id,
      required,
      slots,
      canGrowVertically,
    },
    ref,
  ) => {
    return (
      <>
        <FormComponentWrapperUI
          size={size}
          disabled={disabled}
          className={className}
          ref={ref}
          style={style}
          isError={Boolean(error)}
        >
          {label ? (
            <FormComponentLabelUI size={size} required={required} htmlFor={id}>
              {label}
            </FormComponentLabelUI>
          ) : null}
          <FormComponentBodyUI
            isTransparent={isTransparent}
            size={size}
            isError={Boolean(error)}
            ref={refs.setReference}
            canGrowVertically={canGrowVertically}
            className={styles.body}
            aria-autocomplete="none"
            aria-disabled={disabled}
            aria-invalid={Boolean(error)}
            tabIndex={0}
            {...referenceProps}
          >
            {slots?.before && <FormComponentBodySlotUI>{slots.before}</FormComponentBodySlotUI>}
            {selectedChildren}
            <FormComponentBodySlotUI>
              <SelectChevron isOpen={isOpen} size={size} />
            </FormComponentBodySlotUI>
          </FormComponentBodyUI>
          {typeof error === 'string' && (
            <FormComponentErrorMessageUI>{error}</FormComponentErrorMessageUI>
          )}
        </FormComponentWrapperUI>
        <SelectContext.Provider value={selectContext}>
          <FloatingPortal>
            <FloatingFocusManager context={context} modal={false} disabled={!isOpen}>
              <SelectDropdown
                floatingStyles={floatingStyles}
                floatingProps={floatingProps}
                elementsRef={elementsRef}
                labelsRef={labelsRef}
                size={size}
                isOpen={isOpen}
                refs={refs}
              >
                {children}
              </SelectDropdown>
            </FloatingFocusManager>
          </FloatingPortal>
        </SelectContext.Provider>
      </>
    )
  },
)
